import { Button, Group, NumberInput, Paper, Stack, Text } from "@mantine/core";
import type { FormEvent, RefObject } from "react";

import type { MealFormState } from "./use-meal-form";
import type { FoodItem } from "./food-item-combobox";
import { FoodItemCombobox, formatServingSummary } from "./food-item-combobox";

type MealFormPanelProps = {
  formState: MealFormState;
  isSubmitDisabled: boolean;
  foodInputRef: RefObject<HTMLInputElement | null>;
  quantityInputRef: RefObject<HTMLInputElement | null>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onFoodItemChange: (item: FoodItem | null) => void;
  onQuantityChange: (value: number | string | null | undefined) => void;
  onRequestCreateFoodItem: (name: string) => void;
};

export const MealFormPanel = ({
  formState,
  isSubmitDisabled,
  foodInputRef,
  quantityInputRef,
  onSubmit,
  onFoodItemChange,
  onQuantityChange,
  onRequestCreateFoodItem,
}: MealFormPanelProps) => (
  <form onSubmit={onSubmit}>
    <Paper withBorder={false} shadow={"none"} radius="md" p="lg">
      <Stack gap="md">
        <div>
          <Text fw={600}>Add food item</Text>
          <Text size="sm" c="dimmed">
            Search, adjust quantity, and save to the meal board.
          </Text>
        </div>

        <FoodItemCombobox
          value={formState.foodItem}
          onChange={onFoodItemChange}
          onRequestCreate={onRequestCreateFoodItem}
          placeholder="Search food"
          inputRef={foodInputRef}
        />

        <Group gap="xs" align="flex-end">
          <NumberInput
            ref={quantityInputRef}
            placeholder="Qty"
            min={0.1}
            step={0.1}
            value={formState.quantity ?? ""}
            onChange={onQuantityChange}
            w={120}
          />
          {formState.foodItem?.serving_unit && (
            <Text size="sm" c="dimmed">
              {formState.foodItem.serving_unit}
            </Text>
          )}
        </Group>

        {formState.foodItem && (
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {formState.foodItem.name}
            </Text>
            <Text size="sm" c="dimmed">
              Serving: {formatServingSummary(formState.foodItem)}
            </Text>
            {formState.foodItem.serving_description?.trim() && (
              <Text size="sm" c="dimmed">
                {formState.foodItem.serving_description.trim()}
              </Text>
            )}
          </Stack>
        )}

        <Stack gap="xs">
          <Button type="submit" fullWidth disabled={isSubmitDisabled}>
            Add item to meal
          </Button>
        </Stack>
      </Stack>
    </Paper>
  </form>
);
